import { FiSettings, FiGlobe, FiSun, FiMoon, FiCircle, FiType, FiExternalLink, FiSliders } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { useThemeStore, type Theme } from '../../store/themeStore'
import { useFontStore, type FontFamily, type FontSize } from '../../store/fontStore'
import { useUserDefaultsStore } from '../../store/userDefaultsStore'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import SideSlider from './SideSlider'

interface UserSettingsSliderProps {
  isOpen: boolean
  onClose: () => void
}

const themes: { value: Theme; ar: string; en: string; icon: React.ReactNode }[] = [
  { value: 'light', ar: 'فاتح', en: 'Light', icon: <FiSun /> },
  { value: 'normal', ar: 'عادي', en: 'Normal', icon: <FiCircle /> },
  { value: 'dark', ar: 'داكن', en: 'Dark', icon: <FiMoon /> },
]

const fontSizes: { value: FontSize; ar: string; en: string }[] = [
  { value: 'small', ar: 'صغير', en: 'Small' },
  { value: 'medium', ar: 'متوسط', en: 'Medium' },
  { value: 'large', ar: 'كبير', en: 'Large' },
  { value: 'xlarge', ar: 'كبير جداً', en: 'Extra large' },
]

const fontFamilies: FontFamily[] = [
  'Cairo',
  'Tajawal',
  'Almarai',
  'Amiri',
  'Noto Sans Arabic',
  'IBM Plex Sans Arabic',
  'Arial',
  'Tahoma',
]

export default function UserSettingsSlider({ isOpen, onClose }: UserSettingsSliderProps) {
  const navigate = useNavigate()
  const { language, setLanguage } = useLanguageStore()
  const { theme, setTheme } = useThemeStore()
  const { fontFamily, fontSize, setFontFamily, setFontSize } = useFontStore()
  const setForceWizardOpen = useUserDefaultsStore((s) => s.setForceWizardOpen)
  const isRTL = language === 'ar'

  const applyTheme = (next: Theme) => {
    setTheme(next)
    toast.success(isRTL ? 'تم تحديث المظهر' : 'Theme updated')
  }

  const applyLanguage = (lang: 'ar' | 'en') => {
    if (language === lang) return
    setLanguage(lang)
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = lang
    toast.success(lang === 'ar' ? 'تم تغيير اللغة إلى العربية' : 'Language switched to English')
  }

  return (
    <SideSlider
      isOpen={isOpen}
      onClose={onClose}
      isRTL={isRTL}
      title={
        <span className="flex items-center gap-2">
          <FiSettings />
          {isRTL ? 'الإعدادات' : 'Settings'}
        </span>
      }
      widthClass="max-w-md"
      contentClassName="p-6 space-y-6"
    >
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FiGlobe />
              {isRTL ? 'اللغة' : 'Language'}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {(['ar', 'en'] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => applyLanguage(lang)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    language === lang
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {lang === 'ar' ? 'العربية' : 'English'}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FiSun />
              {isRTL ? 'المظهر' : 'Theme'}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {themes.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => applyTheme(item.value)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium flex flex-col items-center gap-1 transition-colors ${
                    theme === item.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.icon}
                  {isRTL ? item.ar : item.en}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FiType />
              {isRTL ? 'حجم الخط' : 'Font size'}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {fontSizes.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    setFontSize(item.value)
                    toast.success(isRTL ? 'تم تحديث حجم الخط' : 'Font size updated')
                  }}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    fontSize === item.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {isRTL ? item.ar : item.en}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FiType />
              {isRTL ? 'نوع الخط' : 'Font family'}
            </h3>
            <select
              value={fontFamily}
              onChange={(e) => {
                setFontFamily(e.target.value as FontFamily)
                toast.success(isRTL ? 'تم تحديث نوع الخط' : 'Font family updated')
              }}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              style={{ fontFamily }}
            >
              {fontFamilies.map((family) => (
                <option key={family} value={family} style={{ fontFamily: family }}>
                  {family}
                </option>
              ))}
            </select>
          </section>

          <section className="pt-2 border-t border-gray-100 space-y-2">
            <button
              type="button"
              onClick={() => {
                onClose()
                setForceWizardOpen(true)
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
            >
              <FiSliders />
              {isRTL ? 'إعدادات المستخدم الافتراضية' : 'Personal default settings'}
            </button>
            <button
              type="button"
              onClick={() => {
                onClose()
                navigate('/settings')
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FiExternalLink />
              {isRTL ? 'إعدادات النظام' : 'System settings'}
            </button>
          </section>
    </SideSlider>
  )
}
